/**
 * useUserCredits Hook for Real-time Credit Balance Management
 * Tracks user credit balance from Firestore with real-time updates
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';

/**
 * Custom hook for managing user credit balance
 * @returns {Object} Credit state and management functions
 */
export const useUserCredits = () => {
  const { currentUser, loading: authLoading, authError } = useAuth();
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(Date.now());

  // Ref to store the unsubscribe function for cleanup
  const unsubscribeRef = useRef(null);
  const isMountedRef = useRef(true);

  // Helper function to detect if user is newly registered
  const isNewUser = useCallback((user) => {
    if (!user?.metadata?.creationTime || !user?.metadata?.lastSignInTime) {
      return false;
    }

    const createdAt = new Date(user.metadata.creationTime);
    const lastSignIn = new Date(user.metadata.lastSignInTime);
    const now = new Date();
    const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds

    // BOTH creation and last sign-in must be within 5 minutes
    const isRecentlyCreated = (now - createdAt) <= fiveMinutes;
    const isRecentSignIn = (now - lastSignIn) <= fiveMinutes;

    return isRecentlyCreated && isRecentSignIn;
  }, []);

  // Manual refresh function for after purchases
  const refreshCredits = useCallback(async () => {
    if (!currentUser) {
      setCredits(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (isMountedRef.current) {
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setCredits(userData.credits || 0);
          console.log(`🔄 refreshCredits: Loaded credits: ${userData.credits || 0}`);
        } else {
          // User document doesn't exist - check if user is new
          if (currentUser && isNewUser(currentUser)) {
            console.log(`🆕 refreshCredits: New user detected, optimistically showing 10 credits`);
            setCredits(10);
          } else {
            console.log(`⚠️ refreshCredits: User document not found, defaulting to 0 credits`);
            setCredits(0);
          }
        }
        setLastRefresh(Date.now());
        setLoading(false);
      }
    } catch (err) {
      console.error('Error refreshing credits:', err);
      if (isMountedRef.current) {
        setError('Failed to refresh credit balance');
        setLoading(false);
      }
    }
  }, [currentUser, isNewUser]);

  // Set up real-time listener for credit balance
  useEffect(() => {
    // Reset state when auth state changes
    if (authLoading) {
      setLoading(true);
      return;
    }

    if (authError) {
      setError('Authentication error');
      setLoading(false);
      return;
    }

    // No user logged in
    if (!currentUser) {
      setCredits(0);
      setLoading(false);
      setError(null);
      return;
    }

    // Set up Firestore listener for real-time updates
    const setupListener = () => {
      try {
        setLoading(true);
        setError(null);

        const userDocRef = doc(db, 'users', currentUser.uid);
        
        unsubscribeRef.current = onSnapshot(
          userDocRef,
          (doc) => {
            if (!isMountedRef.current) return;

            if (doc.exists()) {
              const userData = doc.data();
              setCredits(userData.credits || 0);
              console.log(`📊 useUserCredits: Real-time update - credits: ${userData.credits || 0}`);
            } else {
              // Document doesn't exist - check if user is new
              if (currentUser && isNewUser(currentUser)) {
                console.log(`🆕 useUserCredits: New user detected, optimistically showing 10 credits`);
                setCredits(10);
              } else {
                console.log(`⚠️ useUserCredits: User document not found, defaulting to 0 credits`);
                setCredits(0);
              }
            }

            setLoading(false);
            setError(null);
          },
          (err) => {
            console.error('Firestore listener error:', err);
            if (!isMountedRef.current) return;

            // Handle different types of errors
            let errorMessage = 'Failed to load credit balance';
            
            if (err.code === 'permission-denied') {
              errorMessage = 'Permission denied accessing credits';
            } else if (err.code === 'unavailable') {
              errorMessage = 'Service temporarily unavailable';
            } else if (err.code === 'unauthenticated') {
              errorMessage = 'Please log in to view credits';
            }

            setError(errorMessage);
            setLoading(false);
          }
        );
      } catch (err) {
        console.error('Error setting up Firestore listener:', err);
        if (isMountedRef.current) {
          setError('Failed to connect to credit service');
          setLoading(false);
        }
      }
    };

    setupListener();

    // Cleanup function
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [currentUser, authLoading, authError, isNewUser]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  // Memoize return object to prevent unnecessary re-renders
  const returnValue = {
    credits: credits,
    loading: loading || authLoading,
    error: error || (authError ? authError.message || 'Authentication error' : null),
    refreshCredits,
    isAuthenticated: !!currentUser,
    lastRefresh
  };

  return returnValue;
};

/**
 * Hook variant that only gets credits once (no real-time updates)
 * Useful for one-time credit checks
 */
export const useUserCreditsOnce = () => {
  const { currentUser } = useAuth();
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const getCreditsOnce = async () => {
      if (!currentUser) {
        setCredits(0);
        setLoading(false);
        return;
      }

      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setCredits(userData.credits || 0);
        } else {
          setCredits(0);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching credits:', err);
        setError('Failed to load credits');
        setLoading(false);
      }
    };

    getCreditsOnce();
  }, [currentUser]);

  return { credits, loading, error };
};

/**
 * Utility function to format credits for display
 * @param {number} credits - Number of credits
 * @returns {string} Formatted credit display
 */
export const formatCredits = (credits) => {
  if (credits === 0) return '0 credits';
  if (credits === 1) return '1 credit';
  return `${credits} credits`;
};

/**
 * Utility function to get credit status color
 * @param {number} credits - Number of credits
 * @returns {string} Tailwind color class
 */
export const getCreditStatusColor = (credits) => {
  if (credits === 0) return 'text-red-600';
  if (credits < 5) return 'text-yellow-600';
  if (credits < 20) return 'text-blue-600';
  return 'text-green-600';
};